import { Injectable, OnDestroy } from '@angular/core';
import * as _ from 'lodash';
import { firstValueFrom, fromEvent, Observable, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ApplicationContextService, KeyDateRefreshOutcome } from 'src/app/services/application-context.service';
import { UrlaFieldDefaultsService } from 'src/app/services/urla-field-defaults/urla-field-defaults.service';
import {
  LoanEditStateService,
  LoanEditorHandle,
  LoanMergeInputs,
  LoanServerModelApplyResult
} from 'src/app/services/loan-edit-state.service';
import { LoanService } from 'src/app/services/loan/loan.service';
import { LoanApplication } from 'src/app/models/loan/loan-info.model';
import { NotificationService } from 'src/app/services/notification.service';
import { SignalRService } from 'src/app/services/signalr.service';
import { LoanConflictResolutionService } from 'src/app/services/loan-conflict-resolution.service';
import { UrlaMortgage } from 'src/app/modules/urla/models/urla-mortgage.model';
import {
  LoanUnmergeablePath,
  MergeReport
} from 'src/app/shared/utils/mortgage/mortgage-path-merge';
import { ClientInstanceService } from 'src/app/core/services/client-instance.service';
import { LOAN_UPDATE_REBASE_SOURCE } from 'src/app/shared/utils/mortgage/loan-rebase-source';
import { RealTimeLoanUpdatesService } from 'src/app/services/real-time-loan-updates.service';

@Injectable({
  providedIn: 'root'
})
export class LoanUpdateCoordinatorService implements OnDestroy {
  private static readonly DIRTY_NOTIFICATION_SUPPRESSION_WINDOW_MS = 60000;
  private static readonly KEY_DATES_SOURCE = 'a background update';
  private _subscription: Subscription | null = null;
  private _openModalCount = 0;
  private _lastSeenOpenApplicationId: number | null = null;
  private readonly _inFlightApplicationIds = new Set<number>();
  private readonly _pendingUpdateApplicationIds = new Map<number, boolean>();
  private readonly _pendingRebaseApplicationIds = new Map<number, boolean>();
  private readonly _dirtyNotificationTimes = new Map<number, number>();
  private readonly _trailingDirtyNotificationTimers = new Map<number, ReturnType<typeof setTimeout>>();
  private readonly _pendingTrailingDirtyNotifications = new Set<number>();

  constructor(
    private readonly _signalRService: SignalRService,
    private readonly _applicationContextService: ApplicationContextService,
    private readonly _loanEditStateService: LoanEditStateService,
    private readonly _notificationService: NotificationService,
    private readonly _loanService: LoanService,
    private readonly _loanConflictResolutionService: LoanConflictResolutionService,
    private readonly _clientInstanceService: ClientInstanceService,
    private readonly _realTimeLoanUpdatesService: RealTimeLoanUpdatesService,
    private readonly _modalService: NgbModal,
    private readonly _urlaFieldDefaults: UrlaFieldDefaultsService
  ) {}

  initialize(): void {
    if (this._subscription) {
      return;
    }

    this._subscription = new Subscription();
    this._subscription.add(
      this._signalRService.applicationUpdated$.subscribe(({ applicationId, originClientId }) => {
        if (typeof originClientId === 'string' && originClientId.length > 0
          && originClientId === this._clientInstanceService.clientId) {
          // Do not fall back to same-user or in-flight-save matching: this event has no reliable signal for either.
          return;
        }

        this.handleApplicationUpdated(applicationId, false);
      })
    );
    this._subscription.add(
      this._signalRService.reconnected$.subscribe(() => {
        const openApplicationId = this._applicationContextService.currentContext?.application?.applicationId ?? null;
        if (openApplicationId !== null) {
          this.handleApplicationUpdated(openApplicationId, true);
        }
      })
    );
    this._subscription.add(
      this._modalService.activeInstances.subscribe(activeInstances => {
        const hadOpenModal = this._openModalCount > 0;
        this._openModalCount = activeInstances.length;
        if (hadOpenModal && this._openModalCount === 0) {
          this.drainPendingUpdates();
        }
      })
    );
    this._subscription.add(
      fromEvent(document, 'visibilitychange').subscribe(() => {
        if (!document.hidden) {
          this.drainPendingUpdates();
        }
      })
    );
    this._subscription.add(
      fromEvent(window, 'focus').subscribe(() => {
        this.drainPendingUpdates();
      })
    );
  }

  ngOnDestroy(): void {
    this._subscription?.unsubscribe();
    this._subscription = null;
    this._openModalCount = 0;
    this._pendingUpdateApplicationIds.clear();
    this._pendingRebaseApplicationIds.clear();
    this._dirtyNotificationTimes.clear();
    this._pendingTrailingDirtyNotifications.clear();
    this._trailingDirtyNotificationTimers.forEach(timer => clearTimeout(timer));
    this._trailingDirtyNotificationTimers.clear();
  }

  async handleUpdateWithLocalEdits(applicationId: number, suppressNotification = false): Promise<void> {
    if (this._inFlightApplicationIds.has(applicationId)) {
      this.deferPendingRebase(applicationId, suppressNotification);
      return;
    }

    this._inFlightApplicationIds.add(applicationId);
    try {
      let application: LoanApplication;
      try {
        application = await firstValueFrom(this._loanService.getApplicationModel(applicationId, true));
      } catch {
        this._notificationService.showError(
          'Failed to load the background update for this loan. Your unsaved changes were kept.',
          'Loan Update'
        );
        return;
      }

      const serverMortgage = application?.mortgageLoan;
      if (!serverMortgage) {
        this._notificationService.showError(
          'Failed to load the background update for this loan. Your unsaved changes were kept.',
          'Loan Update'
        );
        return;
      }

      const dirtyEditors = this._loanEditStateService.getEditors()
        .filter(editor =>
          editor.getChangedPaths().length > 0 ||
          this._loanEditStateService.hasPendingConflict(editor.applicationId)
        );
      if (dirtyEditors.length === 0) {
        await this.refreshCleanLoanAfterUpdate(suppressNotification);
        return;
      }

      // LLS-846: this server version reaches the editors through the merge, not through the context, so it is
      // prepared here (initialize + eligible defaults); the user is told once the merged model is published.
      const preparedDefaults = this._urlaFieldDefaults.prepareServerMortgage(serverMortgage);
      // What the screen showed before the rebase (one runtime editor): defaults it already carried are not news.
      const priorLocalModel = dirtyEditors[0].getLocalModel();
      let mergedMortgage = serverMortgage;
      const conflicts: LoanUnmergeablePath[] = [];
      let conflictOwner: {
        editor: LoanEditorHandle;
        mergeInputs: LoanMergeInputs;
      } | null = null;
      try {
        for (const editor of dirtyEditors) {
          // Chain each editor's merged model so every editor's local edits survive in one mortgage.
          const applyResult: LoanServerModelApplyResult =
            await editor.applyServerModel(mergedMortgage, null, []);
          mergedMortgage = applyResult.report.mergedModel;
          conflicts.push(...applyResult.report.unmergeable);
          if (applyResult.report.unmergeable.length > 0 && !conflictOwner) {
            conflictOwner = {
              editor,
              mergeInputs: applyResult.mergeInputs
            };
          }
        }
        // The editors hold the merged model now, and the publish below reaches their notice consumers
        // synchronously, so the adoption must be recorded first.
        this._urlaFieldDefaults.recordAdopted(preparedDefaults, mergedMortgage, priorLocalModel);
        const applicationWithMergedMortgage = _.cloneDeep(application);
        applicationWithMergedMortgage.mortgageLoan = mergedMortgage;
        await this._applicationContextService.updateMortgageAndApplicationAsync(
          mergedMortgage,
          applicationWithMergedMortgage,
          undefined,
          undefined,
          true,
          LOAN_UPDATE_REBASE_SOURCE
        );
      } catch {
        // Only one editor currently exists; if chaining later expands, earlier editors remain rebased with all local edits.
        this._notificationService.showError(
          'Failed to apply the background update to this loan.',
          'Loan Update'
        );
        return;
      }

      // The merge publish goes through the loan context, which does not refetch key dates.
      this._applicationContextService.refreshKeyDatesFromServer(LoanUpdateCoordinatorService.KEY_DATES_SOURCE).subscribe(outcome => {
        if (outcome === 'failed') {
          this.showKeyDatesRefreshFailure(suppressNotification);
        }
      });

      if (conflicts.length > 0) {
        if (!conflictOwner) {
          throw new Error('Conflict inputs were not retained for the conflicting editor.');
        }
        // A single editor is the current runtime shape; use the editor whose retained inputs the
        // resolution will re-merge from while preserving accumulated conflicts for future multi-editor support.
        this.notifyConflictingLoanUpdate(
          application,
          conflictOwner.editor,
          serverMortgage,
          conflicts,
          conflictOwner.mergeInputs
        );
      } else {
        this.showDirtyUpdateNotification(applicationId, suppressNotification);
      }
    } finally {
      this.completeInFlightApplicationUpdate(applicationId);
    }
  }

  private showDirtyUpdateNotification(applicationId: number, suppressNotification = false): void {
    if (suppressNotification) {
      return;
    }

    if (!this.shouldNotifyDirtyUpdate(applicationId)) {
      this.scheduleTrailingDirtyNotification(applicationId);
      return;
    }

    this._notificationService.showInfo(
      'This loan was updated in the background, and your unsaved changes were kept.',
      'Loan Updated',
      {
        timer: LoanUpdateCoordinatorService.DIRTY_NOTIFICATION_SUPPRESSION_WINDOW_MS,
        timerProgressBar: true,
        showCloseButton: true
      }
    );
  }

  notifyConflictingLoanUpdate(
    application: LoanApplication,
    handle: LoanEditorHandle,
    serverModel: UrlaMortgage,
    conflicts: LoanUnmergeablePath[],
    mergeInputs: LoanMergeInputs
  ): void {
    this._loanConflictResolutionService.presentConflicts({
      handle,
      serverModel,
      serverToken: null,
      mergeInputs,
      conflicts,
      onApplied: async (report: MergeReport<UrlaMortgage>) => {
        try {
          await this._applicationContextService.updateMortgageAndApplicationAsync(
            report.mergedModel,
            application,
            undefined,
            undefined,
            true,
            LOAN_UPDATE_REBASE_SOURCE
          );
        } catch (error: unknown) {
          this._notificationService.showError(
            'Failed to apply the background update to this loan.',
            'Loan Update'
          );
          throw error;
        }
      }
    });
  }

  private shouldNotifyDirtyUpdate(applicationId: number): boolean {
    const now = Date.now();
    const lastNotificationTime = this._dirtyNotificationTimes.get(applicationId);
    if (
      lastNotificationTime !== undefined &&
      now >= lastNotificationTime &&
      now - lastNotificationTime < LoanUpdateCoordinatorService.DIRTY_NOTIFICATION_SUPPRESSION_WINDOW_MS
    ) {
      return false;
    }

    this._dirtyNotificationTimes.set(applicationId, now);
    return true;
  }

  private scheduleTrailingDirtyNotification(applicationId: number): void {
    this._pendingTrailingDirtyNotifications.add(applicationId);
    if (this._trailingDirtyNotificationTimers.has(applicationId)) {
      return;
    }

    const lastNotificationTime = this._dirtyNotificationTimes.get(applicationId) ?? Date.now();
    const delay = Math.max(
      0,
      LoanUpdateCoordinatorService.DIRTY_NOTIFICATION_SUPPRESSION_WINDOW_MS -
        (Date.now() - lastNotificationTime)
    );
    const timer = setTimeout(() => {
      this._trailingDirtyNotificationTimers.delete(applicationId);
      if (!this._pendingTrailingDirtyNotifications.delete(applicationId)) {
        return;
      }
      this._dirtyNotificationTimes.set(applicationId, Date.now());
      this._notificationService.showInfo(
        'This loan was updated in the background again. Your unsaved changes were kept.',
        'Loan Updated',
        {
          timer: LoanUpdateCoordinatorService.DIRTY_NOTIFICATION_SUPPRESSION_WINDOW_MS,
          timerProgressBar: true,
          showCloseButton: true
        }
      );
    }, delay);
    this._trailingDirtyNotificationTimers.set(applicationId, timer);
  }

  private refreshCleanLoanAfterUpdate = async (suppressNotification = false): Promise<void> => {
    try {
      const keyDatesOutcome = await firstValueFrom(this.reloadLoanAndRefreshKeyDates());
      if (!suppressNotification) {
        this.showCleanReloadResult(keyDatesOutcome);
      }
    } catch {
      this._notificationService.showError(
        suppressNotification
          ? 'Failed to refresh this loan.'
          : 'Failed to refresh this loan after it was updated in the background.',
        'Loan Update'
      );
    }
  };

  private handleApplicationUpdated(applicationId: number, suppressNotification = false): void {
    // A company that has not opted in to real-time loan updates gets none of the background applying,
    // rebasing or conflict prompting: the API emits no loan-group events for it either, and its users
    // refresh a loan manually as they did before the feature.
    if (!this._realTimeLoanUpdatesService.enabled) {
      return;
    }

    const openApplicationId = this._applicationContextService.currentContext?.application?.applicationId ?? null;
    this.resetStateForOpenApplication(openApplicationId);

    if (applicationId !== openApplicationId) {
      return;
    }

    if (this._openModalCount > 0) {
      this.deferApplicationUpdate(applicationId, suppressNotification);
      return;
    }

    const isDirty = this._loanEditStateService.isDirty();
    if (document.hidden && isDirty) {
      this.deferApplicationUpdate(applicationId, suppressNotification);
      return;
    }

    this._pendingUpdateApplicationIds.delete(applicationId);

    if (this._inFlightApplicationIds.has(applicationId)) {
      this.deferPendingRebase(applicationId, suppressNotification);
      return;
    }

    if (isDirty) {
      void this.handleUpdateWithLocalEdits(applicationId, suppressNotification).catch(() => {
        this._notificationService.showError(
          'Failed to apply the background update to this loan.',
          'Loan Update'
        );
      });
      return;
    }

    this._inFlightApplicationIds.add(applicationId);
    this.reloadLoanAndRefreshKeyDates().subscribe({
      next: keyDatesOutcome => {
        this.completeInFlightApplicationUpdate(applicationId);
        if (!suppressNotification) {
          this.showCleanReloadResult(keyDatesOutcome);
        }
      },
      error: () => {
        this.completeInFlightApplicationUpdate(applicationId);
        this._notificationService.showError(
          suppressNotification
            ? 'Failed to refresh this loan.'
            : 'Failed to refresh this loan after it was updated in the background.',
          'Loan Update'
        );
      }
    });
  }

  // reloadLoan does not refetch key dates, so a background change to them would otherwise stay invisible.
  private reloadLoanAndRefreshKeyDates(): Observable<KeyDateRefreshOutcome> {
    return this._applicationContextService.reloadLoan().pipe(
      switchMap(() => this._applicationContextService.refreshKeyDatesFromServer(LoanUpdateCoordinatorService.KEY_DATES_SOURCE))
    );
  }

  private showCleanReloadResult(keyDatesOutcome: KeyDateRefreshOutcome): void {
    if (keyDatesOutcome === 'failed') {
      this.showKeyDatesRefreshFailure(false);
      return;
    }
    this._notificationService.showInfo(
      'This loan was updated in the background and has been refreshed.',
      'Loan Updated'
    );
  }

  private showKeyDatesRefreshFailure(suppressNotification: boolean): void {
    if (suppressNotification) {
      return;
    }
    this._notificationService.showError(
      "This loan was updated in the background, but key dates couldn't be refreshed.",
      'Loan Updated'
    );
  }

  private completeInFlightApplicationUpdate(applicationId: number): void {
    this._inFlightApplicationIds.delete(applicationId);
    const pendingSuppressNotification = this._pendingRebaseApplicationIds.get(applicationId);
    this._pendingRebaseApplicationIds.delete(applicationId);
    if (pendingSuppressNotification !== undefined) {
      this.handleApplicationUpdated(applicationId, pendingSuppressNotification);
    }
  }

  private drainPendingUpdates(): void {
    const pendingApplicationIds = [...this._pendingUpdateApplicationIds.entries()];
    this._pendingUpdateApplicationIds.clear();

    for (const [applicationId, suppressNotification] of pendingApplicationIds) {
      this.handleApplicationUpdated(applicationId, suppressNotification);
    }
  }

  private deferApplicationUpdate(applicationId: number, suppressNotification: boolean): void {
    const existingSuppressNotification = this._pendingUpdateApplicationIds.get(applicationId);
    this._pendingUpdateApplicationIds.set(
      applicationId,
      existingSuppressNotification === undefined
        ? suppressNotification
        : existingSuppressNotification && suppressNotification
    );
  }

  private deferPendingRebase(applicationId: number, suppressNotification: boolean): void {
    const existingSuppressNotification = this._pendingRebaseApplicationIds.get(applicationId);
    this._pendingRebaseApplicationIds.set(
      applicationId,
      existingSuppressNotification === undefined
        ? suppressNotification
        : existingSuppressNotification && suppressNotification
    );
  }

  private clearPendingUpdatesForClosedApplication(openApplicationId: number | null): void {
    for (const applicationId of this._pendingUpdateApplicationIds.keys()) {
      if (applicationId !== openApplicationId) {
        this._pendingUpdateApplicationIds.delete(applicationId);
      }
    }
  }

  private resetStateForOpenApplication(openApplicationId: number | null): void {
    if (openApplicationId === this._lastSeenOpenApplicationId) {
      return;
    }

    for (const applicationId of this._inFlightApplicationIds) {
      if (applicationId !== openApplicationId) {
        this._inFlightApplicationIds.delete(applicationId);
      }
    }
    for (const applicationId of this._dirtyNotificationTimes.keys()) {
      if (applicationId !== openApplicationId) {
        this._dirtyNotificationTimes.delete(applicationId);
        this._pendingTrailingDirtyNotifications.delete(applicationId);
        const timer = this._trailingDirtyNotificationTimers.get(applicationId);
        if (timer) {
          clearTimeout(timer);
          this._trailingDirtyNotificationTimers.delete(applicationId);
        }
      }
    }
    for (const applicationId of this._pendingRebaseApplicationIds.keys()) {
      if (applicationId !== openApplicationId) {
        this._pendingRebaseApplicationIds.delete(applicationId);
      }
    }
    this.clearPendingUpdatesForClosedApplication(openApplicationId);

    this._lastSeenOpenApplicationId = openApplicationId;
  }
}
